import { type Component, type JSX, Show, lazy, onMount, Suspense } from 'solid-js'
import { Router, Route, Navigate, useLocation } from '@solidjs/router'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/solid-query'
import { Toaster, notifyError } from '~/lib/toast'
import { AuthProvider, useAuth } from '~/lib/auth'
import { WorkspaceProvider } from '~/lib/workspace'
import { NotificationsProvider } from '~/contexts/NotificationsProvider'
import { WorkspaceShell } from '~/components/layout/WorkspaceShell'
import { getToken } from '~/state/session'
import { I18nProvider, useI18n } from '~/i18n'

// Универсальный fallback для Suspense — единственная общая фраза по всему App.
const AppLoading: Component = () => {
  const { t } = useI18n()
  return <div class="app-loading">{t('common.loading')}</div>
}

// LoginPage — публичная страница, оставлена как есть.
const LoginPage = lazy(() => import('~/pages/LoginPage/LoginPage'))
const LandingPage = lazy(() => import('~/pages/LandingPage/LandingPage'))
const ProfilePage = lazy(() => import('~/pages/ProfilePage/ProfilePage'))
const AccountPage = lazy(() => import('~/pages/AccountPage/AccountPage'))
const OnboardingPage = lazy(() => import('~/pages/OnboardingPage/OnboardingPage'))
const PartnerLandingPage = lazy(() => import('~/pages/PartnerLandingPage/PartnerLandingPage'))
const InviteLandingPage = lazy(() => import('~/pages/InviteLandingPage/InviteLandingPage'))

// Обёртки разделов (рендерят <Outlet/> через children, делают access-check).
const MonitoringPage = lazy(() =>
  import('~/pages/MonitoringPage/MonitoringPage').then((m) => ({ default: m.MonitoringPage })),
)
const PlanningPage = lazy(() =>
  import('~/pages/PlanningPage/PlanningPage').then((m) => ({ default: m.PlanningPage })),
)
const ConfigurationPage = lazy(() =>
  import('~/pages/ConfigurationPage/ConfigurationPage').then((m) => ({ default: m.ConfigurationPage })),
)
const SystemPage = lazy(() =>
  import('~/pages/SystemPage/SystemPage').then((m) => ({ default: m.SystemPage })),
)

// Monitoring sub-pages
const MonitoringProcessing = lazy(() => import('~/pages/MonitoringPage/Processing/ProcessingPage'))
const MonitoringTransport = lazy(() => import('~/pages/MonitoringPage/Transport/TransportPage'))
const MonitoringCargo = lazy(() => import('~/pages/MonitoringPage/Cargo/CargoPage'))
const MonitoringStats = lazy(() => import('~/pages/MonitoringPage/Stats/StatsPage'))
const MonitoringMessagesQueue = lazy(() => import('~/pages/MonitoringPage/MessagesQueue/MessagesQueuePage'))

// Terminal pages (вне ProtectedShell)
const InviteLinkLanding = lazy(() => import('~/pages/terminal/InviteLinkLandingPage/InviteLinkLandingPage'))
const MaxTerminal = lazy(() => import('~/pages/terminal/MaxTerminalPage/MaxTerminalPage'))
const TerminalEntry = lazy(() => import('~/pages/terminal/TerminalEntry/TerminalEntry'))
const DriverRegTerminal = lazy(() => import('~/pages/terminal/DriverRegTerminalPage/DriverRegTerminalPage'))
const QueueTerminal = lazy(() => import('~/pages/terminal/QueueTerminalPage/QueueTerminalPage'))
const InfoTerminal = lazy(() => import('~/pages/terminal/InfoTerminalPage/InfoTerminalPage'))
const StaffTerminal = lazy(() => import('~/pages/terminal/StaffTerminalPage/StaffTerminalPage'))
const StaffDemo2 = lazy(() => import('~/pages/terminal/StaffDemo2Page/StaffDemo2Page'))
const ScanTest = lazy(() => import('~/pages/terminal/ScanTestPage/ScanTestPage'))
const DeviceSettings = lazy(() => import('~/pages/terminal/DeviceSettingsPage/DeviceSettingsPage'))
const Gs1Detail = lazy(() => import('~/pages/terminal/Gs1DetailPage/Gs1DetailPage'))
const DriverWorkspace = lazy(() => import('~/pages/DriverWorkspacePage/DriverWorkspacePage'))
const DriverProfile = lazy(() => import('~/pages/DriverWorkspacePage/DriverProfilePage'))

// Planning sub-pages
const PlanningTimeslots = lazy(() => import('~/pages/PlanningPage/Timeslots/TimeslotsPage'))
const PlanningShipping = lazy(() => import('~/pages/PlanningPage/Shipping/ShippingPage'))
const PlanningSupply = lazy(() => import('~/pages/PlanningPage/Supply/SupplyPage'))

// Configuration sub-pages
const ConfigurationMyData = lazy(() => import('~/pages/ConfigurationPage/MyData/MyDataPage'))
const ConfigurationComposition = lazy(() => import('~/pages/ConfigurationPage/Composition/CompositionPage'))
const ConfigurationQueues = lazy(() => import('~/pages/ConfigurationPage/Queues/QueuesPage'))
const ConfigurationDirections = lazy(() => import('~/pages/ConfigurationPage/Directions/DirectionsPage'))

// System sub-pages
const SystemObjects = lazy(() => import('~/pages/SystemPage/Objects/ObjectsPage'))
const SystemCounterparties = lazy(() => import('~/pages/SystemPage/Counterparties/CounterpartiesPage'))
const SystemUsers = lazy(() => import('~/pages/SystemPage/Users/UsersPage'))
const SystemIntegrations = lazy(() => import('~/pages/SystemPage/Integrations/IntegrationsPage'))
const SystemAdministration = lazy(() => import('~/pages/SystemPage/Administration/AdministrationPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
  // Глобальный обработчик ошибок: любой упавший запрос/мутация всплывает
  // тостом-ошибкой. Отключить точечно — `meta: { skipGlobalError: true }`.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.skipGlobalError) {
        return
      }
      notifyError(error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Если у мутации есть собственный onError (он уже покажет тост) или явный
      // opt-out — глобальный обработчик молчит, чтобы не задвоить уведомление.
      if (mutation.options.onError || mutation.meta?.skipGlobalError) {
        return
      }
      notifyError(error)
    },
  }),
})

/** Whitelist путей, доступных пока онбординг не пройден. Профиль и Аккаунт —
 *  единственные «безопасные» разделы: оттуда нельзя дойти до данных, которых
 *  ещё нет. /onboarding — сама страница онбординга. */
const ONBOARDING_WHITELIST = ['/onboarding', '/profile', '/account']

const ProtectedShell: Component<{ children: JSX.Element }> = (props) => {
  const { meQuery } = useAuth()
  const location = useLocation()

  // AuthProvider живёт на уровне App, поэтому при первом монтировании (до логина)
  // meQuery кэширует null. После входа в Login токен попадает в sessionStorage,
  // а Router переходит сюда — но кэшированный null остаётся. Форсируем refetch,
  // если в storage есть токен, а данных в кэше нет.
  onMount(() => {
    if (getToken() && !meQuery.data) {
      meQuery.refetch()
    }
  })

  const loadingOrPending = () =>
    meQuery.isLoading ||
    meQuery.isFetching ||
    (Boolean(getToken()) && !meQuery.data)

  // Онбординг-гейт: если у аккаунта нет ни одной локации (или у меня нет к ним
  // доступа) — пускаем только в whitelist. Любой другой путь редиректим на
  // /onboarding (там сама страница решит, что показать — мастер или текст ожидания).
  const needsOnboarding = () => {
    const ob = meQuery.data?.onboarding
    if (!ob) return false
    if (!ob.account_has_any_location) return true
    if (!ob.membership_has_any_accessible_location) return true
    return false
  }
  const inWhitelist = () => {
    const p = location.pathname
    return ONBOARDING_WHITELIST.some((w) => p === w || p.startsWith(`${w}/`))
  }

  return (
    <Show
      when={meQuery.isSuccess && meQuery.data !== null}
      fallback={
        <Show
          when={loadingOrPending()}
          fallback={
            <Suspense>
              <Navigate
                href={`/login${location.pathname !== '/' ? `?next=${location.pathname}` : ''}`}
              />
            </Suspense>
          }
        >
          <AppLoading />
        </Show>
      }
    >
      <WorkspaceProvider>
        <NotificationsProvider>
          <Show
            when={needsOnboarding() && !inWhitelist()}
            fallback={<WorkspaceShell>{props.children}</WorkspaceShell>}
          >
            <Navigate href="/onboarding" />
          </Show>
        </NotificationsProvider>
      </WorkspaceProvider>
    </Show>
  )
}

// /service остаётся как редирект на / — существующий LoginPage делает navigate('/service').
const ServiceRedirect: Component = () => <Navigate href="/" />

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AuthProvider>
          <Toaster position="top-right" />
          <Router>
          <Route path="/login" component={LoginPage} />
          <Route path="/service" component={ServiceRedirect} />
          {/* Партнёрская страница — публичная, без ProtectedShell и без шапки. */}
          <Route
            path="/p/:token"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <PartnerLandingPage />
              </Suspense>
            )}
          />
          {/* Приглашение сотрудника — публичная страница установки пароля. */}
          <Route
            path="/invite/:token"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <InviteLandingPage />
              </Suspense>
            )}
          />
          {/* Terminal pages — публичные, без ProtectedShell. */}
          <Route
            path="/t/i/:code"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <InviteLinkLanding />
              </Suspense>
            )}
          />
          <Route
            path="/t/max"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <MaxTerminal />
              </Suspense>
            )}
          />
          <Route
            path="/t"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <TerminalEntry />
              </Suspense>
            )}
          />
          <Route
            path="/t/driver-reg"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <DriverRegTerminal />
              </Suspense>
            )}
          />
          <Route
            path="/t/queue"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <QueueTerminal />
              </Suspense>
            )}
          />
          <Route
            path="/t/info"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <InfoTerminal />
              </Suspense>
            )}
          />
          <Route
            path="/t/staff"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <StaffTerminal />
              </Suspense>
            )}
          />
          <Route
            path="/t/staff-demo-2"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <StaffDemo2 />
              </Suspense>
            )}
          />
          <Route
            path="/t/scan-test"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <ScanTest />
              </Suspense>
            )}
          />
          <Route
            path="/t/device"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <DeviceSettings />
              </Suspense>
            )}
          />
          <Route
            path="/t/gs1/:barcode"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <Gs1Detail />
              </Suspense>
            )}
          />
          <Route
            path="/d"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <DriverWorkspace />
              </Suspense>
            )}
          />
          <Route
            path="/d/profile"
            component={() => (
              <Suspense fallback={<AppLoading />}>
                <DriverProfile />
              </Suspense>
            )}
          />
          <Route
            path="/"
            component={(p) => (
              <ProtectedShell>
                <Suspense fallback={<AppLoading />}>
                  {p.children}
                </Suspense>
              </ProtectedShell>
            )}
          >
            <Route path="/" component={LandingPage} />

            <Route path="/monitoring" component={MonitoringPage}>
              <Route path="/" component={() => <Navigate href="/monitoring/processing" />} />
              <Route path="/processing" component={MonitoringProcessing} />
              <Route path="/transport" component={MonitoringTransport} />
              <Route path="/cargo" component={MonitoringCargo} />
              <Route path="/stats" component={MonitoringStats} />
              <Route path="/messages-queue" component={MonitoringMessagesQueue} />
            </Route>

            <Route path="/planning" component={PlanningPage}>
              <Route path="/" component={() => <Navigate href="/planning/timeslots" />} />
              <Route path="/timeslots" component={PlanningTimeslots} />
              <Route path="/shipping" component={PlanningShipping} />
              <Route path="/supply" component={PlanningSupply} />
            </Route>

            <Route path="/configuration" component={ConfigurationPage}>
              <Route path="/" component={() => <Navigate href="/configuration/my-data" />} />
              <Route path="/my-data" component={ConfigurationMyData} />
              <Route path="/composition" component={ConfigurationComposition} />
              <Route path="/queues" component={ConfigurationQueues} />
              <Route path="/directions" component={ConfigurationDirections} />
            </Route>

            <Route path="/system" component={SystemPage}>
              <Route path="/" component={() => <Navigate href="/system/objects" />} />
              <Route path="/objects" component={SystemObjects} />
              <Route path="/counterparties" component={SystemCounterparties} />
              <Route path="/users" component={SystemUsers} />
              <Route path="/integrations" component={SystemIntegrations} />
              <Route path="/administration" component={SystemAdministration} />
            </Route>

            <Route path="/profile" component={ProfilePage} />
            <Route path="/account" component={AccountPage} />
            <Route path="/onboarding" component={OnboardingPage} />
          </Route>
        </Router>
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  )
}
